// src/components/shifts/ShiftForm.js
import React, { useState, useEffect } from 'react';
import SearchableSelect from '../common/SearchableSelect';

const ShiftForm = ({ shift, premises, onSubmit, onClose, user }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const isAppAdmin = user?.user_type === 'app_admin';
    const isCoreAdmin = user?.user_type === 'core_admin';
    const [formData, setFormData] = useState({
        shift_name: '',
        premises_id: '',
        premises_start_time: '',
        premises_end_time: '',
        premises_start_time_margin: '00:15:00',
        premises_end_time_margin: '00:15:00',
        premises_late_timein_margin: '00:10:00',
        premises_early_timeout_margin: '00:10:00',
        apply_to_all_premises: false,
        status: 1
    });

    useEffect(() => {
        if (shift) {
            setFormData({
                shift_name: shift.shift_name || '',
                premises_id: shift.premises_id || '',
                premises_start_time: shift.premises_start_time || '',
                premises_end_time: shift.premises_end_time || '',
                premises_start_time_margin: shift.premises_start_time_margin || '00:15:00',
                premises_end_time_margin: shift.premises_end_time_margin || '00:15:00',
                premises_late_timein_margin: shift.premises_late_timein_margin || '00:10:00',
                premises_early_timeout_margin: shift.premises_early_timeout_margin || '00:10:00',
                apply_to_all_premises: false,
                status: shift.status !== undefined ? shift.status : 1
            });
        }
    }, [shift]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const getShiftDuration = () => {
        if (!formData.premises_start_time || !formData.premises_end_time) return '';

        try {
            const start = new Date(`2000-01-01T${formData.premises_start_time}`);
            const end = new Date(`2000-01-01T${formData.premises_end_time}`);

            if (end < start) {
                end.setDate(end.getDate() + 1); // Handle overnight shifts
            }

            const diffMs = end - start;
            const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
            const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

            return `${diffHours}h ${diffMinutes}m`;
        } catch {
            return '';
        }
    };

    const validateForm = () => {
        const errors = [];

        if (!formData.shift_name.trim()) {
            errors.push('Shift name is required');
        }

        if (!formData.premises_start_time) {
            errors.push('Start time is required');
        }

        if (!formData.premises_end_time) {
            errors.push('End time is required');
        }

        // Validate premises only if not applying to all premises
        if (!formData.apply_to_all_premises && !formData.premises_id && !shift) {
            errors.push(isAppAdmin
                ? 'Please select a premises'
                : 'Please select a premises or check "Apply to All Premises"');
        }

        return errors;
    };

    const premisesOptions = premises.map((premise) => {
        const orgName = premise.organization_name || premise.org_name || premise.organization || '';
        const displayName = isCoreAdmin && orgName
            ? `${premise.premises_name} (${orgName})`
            : premise.premises_name;
        return {
            id: String(premise.id),
            name: displayName
        };
    });

    const handleSubmit = async (e) => {
        e.preventDefault();

        const validationErrors = validateForm();
        if (validationErrors.length > 0) {
            setError(validationErrors.join(', '));
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const submitData = {
                ...formData,
                apply_to_all_premises: isAppAdmin ? false : formData.apply_to_all_premises,
                premises_id: formData.premises_id || 0
            };

            await onSubmit(submitData);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal-overlay">
            <div className="modal shift-form-modal">
                <div className="modal-header">
                    <h3>
                        {shift ? 'Edit Shift' : 'Create New Shift'}
                    </h3>
                    <button className="close-btn" onClick={onClose} type="button">
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                <div className="modal-body">
                    <form onSubmit={handleSubmit} className="shift-form-content">
                        {error && (
                            <div className="error-message">
                                <i className="fas fa-exclamation-circle"></i>
                                {error}
                            </div>
                        )}

                        <div className="form-sections">
                            {/* Basic Information */}
                            <div className="form-section">
                                <h4>
                                    <i className="fas fa-info-circle"></i>
                                    Basic Information
                                </h4>

                                <div className="form-grid">
                                    <div className="form-group">
                                        <label>Shift Name *</label>
                                        <input
                                            type="text"
                                            className="form-input"
                                            value={formData.shift_name}
                                            onChange={(e) => handleInputChange('shift_name', e.target.value)}
                                            placeholder="Enter shift name (e.g., 1st Shift, 2nd Shift)"
                                            required
                                        />
                                    </div>

                                    {/* Apply to All Premises Checkbox */}
                                    {!shift && !isAppAdmin && (
                                        <div className="form-group full-width">
                                            <label className="apply-all-checkbox">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.apply_to_all_premises}
                                                    onChange={(e) => handleInputChange('apply_to_all_premises', e.target.checked)}
                                                />
                                                <span className="checkbox-icon">
                                                    <i className="fas fa-building"></i>
                                                </span>
                                                <span className="checkbox-label-text">
                                                    Apply to All Premises in Organization
                                                </span>
                                            </label>
                                            <p className="form-help">
                                                {formData.apply_to_all_premises
                                                    ? 'This shift will be created for all premises in your organization'
                                                    : 'Uncheck to select a specific premises'}
                                            </p>
                                        </div>
                                    )}

                                    {/* Premises Selector - hidden when apply_to_all_premises is checked */}
                                    {!formData.apply_to_all_premises && (
                                        <div className="form-group">
                                            <label>Premises {!shift && '*'}</label>
                                            {isCoreAdmin ? (
                                                <SearchableSelect
                                                    options={premisesOptions}
                                                    value={String(formData.premises_id || '')}
                                                    onChange={(value) => handleInputChange('premises_id', String(value))}
                                                    placeholder="Select Premises"
                                                    displayKey="name"
                                                    valueKey="id"
                                                    searchPlaceholder="Search premises or organization..."
                                                    className="shift-premises-search"
                                                />
                                            ) : (
                                                <select
                                                    className="form-input"
                                                    value={formData.premises_id}
                                                    onChange={(e) => handleInputChange('premises_id', e.target.value)}
                                                >
                                                    <option value="">Select Premises</option>
                                                    {premises.map(premise => (
                                                        <option key={premise.id} value={premise.id}>
                                                            {premise.premises_name}
                                                        </option>
                                                    ))}
                                                </select>
                                            )}
                                            <p className="form-help">
                                                Select the premises for this shift
                                            </p>
                                        </div>
                                    )}

                                    <div className="form-group">
                                        <label className="checkbox-label">
                                            <input
                                                type="checkbox"
                                                checked={formData.status === 1}
                                                onChange={(e) => handleInputChange('status', e.target.checked ? 1 : 0)}
                                            />
                                            Active Shift
                                        </label>
                                        <p className="form-help">
                                            Inactive shifts cannot be assigned to staff
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Time Configuration */}
                            <div className="form-section">
                                <h4>
                                    <i className="fas fa-clock"></i>
                                    Time Configuration
                                </h4>

                                <div className="form-grid">
                                    <div className="form-group">
                                        <label>Start Time *</label>
                                        <input
                                            type="time"
                                            className="form-input"
                                            value={formData.premises_start_time}
                                            onChange={(e) => handleInputChange('premises_start_time', e.target.value)}
                                            required
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>End Time *</label>
                                        <input
                                            type="time"
                                            className="form-input"
                                            value={formData.premises_end_time}
                                            onChange={(e) => handleInputChange('premises_end_time', e.target.value)}
                                            required
                                        />
                                    </div>

                                    {getShiftDuration() && (
                                        <div className="duration-display">
                                            <div className="duration-card">
                                                <h5>Shift Duration</h5>
                                                <div className="duration-value">{getShiftDuration()}</div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Time Margins */}
                            <div className="form-section">
                                <h4>
                                    <i className="fas fa-hourglass-half"></i>
                                    Time Margins
                                </h4>

                                <div className="form-grid">
                                    <div className="form-group">
                                        <label>Start Time Margin (HH:MM:SS)</label>
                                        <input
                                            type="text"
                                            step="1"
                                            className="form-input"
                                            value={formData.premises_start_time_margin}
                                            onChange={(e) => handleInputChange('premises_start_time_margin', e.target.value)}
                                        />
                                        <p className="form-help">
                                            Grace period before shift start time
                                        </p>
                                    </div>

                                    <div className="form-group">
                                        <label>End Time Margin (HH:MM:SS)</label>
                                        <input
                                            type="text"
                                            step="1"
                                            className="form-input"
                                            value={formData.premises_end_time_margin}
                                            onChange={(e) => handleInputChange('premises_end_time_margin', e.target.value)}
                                        />
                                        <p className="form-help">
                                            Grace period after shift end time
                                        </p>
                                    </div>

                                    <div className="form-group">
                                        <label>Late Time-in Margin (HH:MM:SS)</label>
                                        <input
                                            type="text"
                                            step="1"
                                            className="form-input"
                                            value={formData.premises_late_timein_margin}
                                            onChange={(e) => handleInputChange('premises_late_timein_margin', e.target.value)}
                                        />
                                        <p className="form-help">
                                            Maximum allowed late check-in
                                        </p>
                                    </div>

                                    <div className="form-group">
                                        <label>Early Time-out Margin (HH:MM:SS)</label>
                                        <input
                                            type="text"
                                            step="1"
                                            className="form-input"
                                            value={formData.premises_early_timeout_margin}
                                            onChange={(e) => handleInputChange('premises_early_timeout_margin', e.target.value)}
                                        />
                                        <p className="form-help">
                                            Maximum allowed early check-out
                                        </p>
                                    </div>
                                </div>
                            </div>

                        </div>

                        <div className="modal-actions">
                            <button type="button" className="btn btn-secondary" onClick={onClose}>
                                Cancel
                            </button>
                            <button type="submit" className="btn btn-primary" disabled={loading}>
                                {loading ? (
                                    <>
                                        <i className="fas fa-spinner fa-spin"></i>
                                        {shift ? 'Updating...' : 'Creating...'}
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-save"></i>
                                        {shift ? 'Update Shift' : 'Create Shift'}
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <style jsx>{`
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    padding: var(--spacing-md);
                }

                .shift-form-modal {
                    background: var(--white);
                    border-radius: var(--radius-xl);
                    box-shadow: var(--shadow-lg);
                    width: 100%;
                    max-width: 980px;
                    max-height: calc(100vh - 2rem);
                    height: auto;
                    overflow: hidden;
                    display: flex;
                    flex-direction: column;
                }

                .modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: var(--spacing-xl);
                    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
                    background: var(--light-color);
                }

                .modal-header h3 {
                    margin: 0;
                    color: var(--text-dark);
                    font-size: 1.3rem;
                }

                .close-btn {
                    background: none;
                    border: none;
                    font-size: 1.2rem;
                    cursor: pointer;
                    color: var(--text-light);
                    padding: var(--spacing-xs);
                    border-radius: var(--radius-sm);
                    transition: all 0.2s ease;
                }

                .close-btn:hover {
                    color: var(--text-dark);
                    background: rgba(0, 0, 0, 0.05);
                }

                .modal-body {
                    padding: var(--spacing-xl);
                    overflow-y: auto;
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    min-height: 0;
                }

                .shift-form-content {
                    display: flex;
                    flex-direction: column;
                    gap: var(--spacing-lg);
                }

                .error-message {
                    background: rgba(239, 68, 68, 0.1);
                    color: var(--error);
                    padding: var(--spacing-md);
                    border-radius: var(--radius-md);
                    margin-bottom: var(--spacing-lg);
                    display: flex;
                    align-items: center;
                    gap: var(--spacing-sm);
                    font-size: 0.9rem;
                }

                .form-sections {
                    display: flex;
                    flex-direction: column;
                    gap: var(--spacing-xl);
                    min-width: 0;
                }

                .form-section {
                    border: 1px solid rgba(0, 0, 0, 0.05);
                    border-radius: var(--radius-lg);
                    overflow: visible;
                }

                .form-section h4 {
                    background: var(--light-color);
                    color: var(--text-dark);
                    margin: 0;
                    padding: var(--spacing-lg);
                    font-size: 1.1rem;
                    display: flex;
                    align-items: center;
                    gap: var(--spacing-sm);
                    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
                }

                .form-section h4 i {
                    color: var(--admin-primary);
                }

                .form-grid {
                    display: grid;
                    grid-template-columns: repeat(2, minmax(0, 1fr));
                    gap: var(--spacing-lg);
                    padding: var(--spacing-lg);
                    align-items: start;
                }

                .form-group {
                    display: flex;
                    flex-direction: column;
                    gap: var(--spacing-sm);
                    min-width: 0;
                    position: relative;
                }

                .form-group.full-width {
                    grid-column: 1 / -1;
                }

                .form-group label {
                    font-weight: 600;
                    color: var(--text-dark);
                    font-size: 0.9rem;
                }

                .form-input {
                    padding: var(--spacing-md);
                    border: 1px solid rgba(0, 0, 0, 0.1);
                    border-radius: var(--radius-md);
                    font-size: 0.9rem;
                    transition: border-color 0.2s ease, box-shadow 0.2s ease;
                    background: var(--white);
                }

                .form-input:focus {
                    outline: none;
                    border-color: var(--admin-primary);
                    box-shadow: 0 0 0 3px rgba(var(--admin-primary-rgb), 0.1);
                }

                .shift-premises-search {
                    width: 100%;
                }

                .shift-premises-search .searchable-select-input {
                    min-height: 44px;
                    width: 100%;
                }

                .shift-premises-search .searchable-select-dropdown {
                    z-index: 1200;
                }

                .form-help {
                    font-size: 0.8rem;
                    color: var(--text-light);
                    margin: var(--spacing-xs) 0 0 0;
                    line-height: 1.4;
                }

                .checkbox-label {
                    display: flex;
                    align-items: center;
                    gap: var(--spacing-sm);
                    font-size: 0.9rem;
                    cursor: pointer;
                    padding: var(--spacing-sm);
                    border-radius: var(--radius-sm);
                    transition: background-color 0.2s ease;
                }

                .checkbox-label:hover {
                    background: rgba(var(--admin-primary-rgb), 0.05);
                }

                .checkbox-label input[type="checkbox"] {
                    accent-color: var(--admin-primary);
                    margin: 0;
                }

                .apply-all-checkbox {
                    display: flex;
                    align-items: center;
                    gap: 0.6rem;
                    padding: 0.65rem 0.8rem;
                    background: #f8fbff;
                    border: 1px solid #cfe3ff;
                    border-radius: var(--radius-lg);
                    cursor: pointer;
                    transition: all 0.3s ease;
                    width: 100%;
                    min-width: 0;
                }

                .apply-all-checkbox:hover {
                    border-color: #9dc7ff;
                    box-shadow: 0 2px 8px rgba(14, 165, 233, 0.1);
                }

                .apply-all-checkbox input[type="checkbox"] {
                    width: 16px;
                    height: 16px;
                    cursor: pointer;
                    accent-color: var(--admin-primary);
                    margin: 0;
                    flex-shrink: 0;
                }

                .checkbox-icon {
                    display: flex;
                    align-items: center;
                    font-size: 0.95rem;
                    color: #0284c7;
                    flex-shrink: 0;
                }

                .checkbox-label-text {
                    font-weight: 600;
                    color: #0c4a6e;
                    font-size: 0.88rem;
                    line-height: 1.3;
                    white-space: normal;
                    word-break: break-word;
                }

                .shift-type-selector {
                    grid-column: 1 / -1;
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: var(--spacing-md);
                    margin-top: var(--spacing-sm);
                }

                .type-option {
                    position: relative;
                    cursor: pointer;
                }

                .type-option input[type="radio"] {
                    position: absolute;
                    opacity: 0;
                }

                .type-card {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: var(--spacing-sm);
                    padding: var(--spacing-lg);
                    border: 2px solid rgba(0, 0, 0, 0.1);
                    border-radius: var(--radius-lg);
                    transition: all 0.2s ease;
                    background: var(--white);
                }

                .type-option input[type="radio"]:checked + .type-card {
                    border-color: var(--admin-primary);
                    background: rgba(var(--admin-primary-rgb), 0.05);
                }

                .type-card i {
                    font-size: 1.5rem;
                    color: var(--admin-primary);
                }

                .type-card span {
                    font-size: 0.85rem;
                    font-weight: 500;
                    color: var(--text-dark);
                    text-align: center;
                }

                .duration-display {
                    grid-column: 1 / -1;
                    display: flex;
                    justify-content: center;
                }

                .duration-card {
                    background: rgba(var(--admin-primary-rgb), 0.05);
                    border: 1px solid rgba(var(--admin-primary-rgb), 0.2);
                    border-radius: var(--radius-lg);
                    padding: var(--spacing-lg);
                    text-align: center;
                    min-width: 200px;
                }

                .duration-card h5 {
                    margin: 0 0 var(--spacing-sm) 0;
                    color: var(--text-dark);
                    font-size: 0.9rem;
                }

                .duration-value {
                    font-size: 1.8rem;
                    font-weight: bold;
                    color: var(--admin-primary);
                    margin-bottom: var(--spacing-sm);
                }

                .duration-details p {
                    margin: var(--spacing-xs) 0;
                    font-size: 0.8rem;
                    color: var(--text-light);
                }

                .working-days-section {
                    padding: var(--spacing-lg);
                }

                .preset-buttons {
                    margin-bottom: var(--spacing-lg);
                }

                .preset-buttons label {
                    font-weight: 600;
                    color: var(--text-dark);
                    font-size: 0.9rem;
                    margin-bottom: var(--spacing-sm);
                    display: block;
                }

                .preset-options {
                    display: flex;
                    gap: var(--spacing-sm);
                    flex-wrap: wrap;
                }

                .preset-btn {
                    padding: var(--spacing-xs) var(--spacing-sm);
                    border: 1px solid var(--admin-primary);
                    background: var(--white);
                    color: var(--admin-primary);
                    border-radius: var(--radius-md);
                    cursor: pointer;
                    font-size: 0.8rem;
                    transition: all 0.2s ease;
                }

                .preset-btn:hover {
                    background: var(--admin-primary);
                    color: white;
                }

                .weekdays-grid {
                    display: grid;
                    grid-template-columns: repeat(7, 1fr);
                    gap: var(--spacing-sm);
                }

                .day-option {
                    position: relative;
                    cursor: pointer;
                }

                .day-option input[type="checkbox"] {
                    position: absolute;
                    opacity: 0;
                }

                .day-card {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: var(--spacing-xs);
                    padding: var(--spacing-md);
                    border: 2px solid rgba(0, 0, 0, 0.1);
                    border-radius: var(--radius-lg);
                    transition: all 0.2s ease;
                    background: var(--white);
                }

                .day-option input[type="checkbox"]:checked + .day-card {
                    border-color: var(--admin-primary);
                    background: rgba(var(--admin-primary-rgb), 0.1);
                }

                .day-short {
                    font-weight: bold;
                    color: var(--admin-primary);
                    font-size: 0.9rem;
                }

                .day-full {
                    font-size: 0.7rem;
                    color: var(--text-light);
                    text-align: center;
                }

                .modal-actions {
                    display: flex;
                    justify-content: flex-end;
                    gap: var(--spacing-sm);
                    padding-top: var(--spacing-md);
                    margin-top: var(--spacing-sm);
                    border-top: 1px solid rgba(0, 0, 0, 0.1);
                    background: var(--light-color);
                    position: sticky;
                    bottom: 0;
                    z-index: 10;
                }

                .btn {
                    padding: var(--spacing-sm) var(--spacing-lg);
                    border: none;
                    border-radius: var(--radius-md);
                    font-size: 0.9rem;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: var(--spacing-sm);
                    min-width: 120px;
                }

                .btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .btn-primary {
                    background: var(--admin-primary);
                    color: white;
                }

                .btn-primary:hover:not(:disabled) {
                    background: var(--admin-secondary);
                    transform: translateY(-1px);
                }

                .btn-secondary {
                    background: var(--white);
                    color: var(--text-dark);
                    border: 1px solid rgba(0, 0, 0, 0.1);
                }

                .btn-secondary:hover:not(:disabled) {
                    background: #f8f9fa;
                    border-color: var(--admin-primary);
                }

                .fa-spinner {
                    animation: spin 1s linear infinite;
                }

                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }

                /* Responsive Design */
                @media (max-width: 768px) {
                    .modal-overlay {
                        padding: var(--spacing-sm);
                    }

                    .shift-form-modal {
                        max-width: 100%;
                        max-height: calc(100vh - 0.75rem);
                    }

                    .modal-header,
                    .modal-body {
                        padding: var(--spacing-lg);
                    }

                    .form-grid {
                        grid-template-columns: 1fr;
                        padding: var(--spacing-md);
                    }

                    .shift-type-selector {
                        grid-template-columns: repeat(2, 1fr);
                    }

                    .weekdays-grid {
                        grid-template-columns: repeat(4, 1fr);
                    }

                    .preset-options {
                        flex-direction: column;
                    }

                    .modal-actions {
                        flex-direction: column;
                        padding: var(--spacing-md);
                    }

                    .btn {
                        width: 100%;
                        justify-content: center;
                    }
                }

                @media (max-width: 480px) {
                    .shift-type-selector {
                        grid-template-columns: 1fr;
                    }

                    .weekdays-grid {
                        grid-template-columns: repeat(3, 1fr);
                    }
                }
            `}</style>
        </div>
    );
};

export default ShiftForm;
